'use client'

/**
 * SessionGuard — detects when an SSO user's Entra id_token has been lost
 * (expired and refresh failed, or container restart wiped the token store)
 * and raises a persistent toast (lib/guard-toasts.tsx) prompting re-authentication.
 *
 * Why a toast and not automatic signIn(): calling signIn() silently would
 * interrupt any active chat stream. The toast lets the user choose the right
 * moment to re-auth. Because Entra SSO is instant for users with an active
 * browser session, the round-trip is typically < 2 seconds.
 *
 * Detection: is_sso=true && id_token=undefined && status=authenticated.
 * We wait 1 second before showing the toast to avoid false positives
 * during initial page load (before the session has fully hydrated).
 *
 * Anti-loop notes (2026-07-21):
 *   - Ignore status==='loading'. useSession().update() flips loading while
 *     keeping the prior session object; reacting to that used to clear/re-arm
 *     this toast on every update().
 *   - Debounce token recovery. Multi-instance token-store races can briefly
 *     return an id_token on one poll and miss it on the next; treating each
 *     blip as "recovered" reset `dismissed` and made the toast reappear.
 */

import { useEffect, useRef, useState } from 'react'
import { useSession, signOut } from 'next-auth/react'
import { GUARD_TOAST_ID, hideGuardToast, showSessionToast } from '@/lib/guard-toasts'

const SHOW_DELAY_MS = 1_000
/** Token must stay present this long before we treat recovery as real. */
const RECOVERY_DEBOUNCE_MS = 2_000

export function SessionGuard() {
  const { data: session, status } = useSession()
  const [show, setShow] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isSso = (session as { is_sso?: boolean } | null)?.is_sso === true
  const hasToken = Boolean((session as { id_token?: string } | null)?.id_token)
  const needsReauth = status === 'authenticated' && isSso && !hasToken

  useEffect(() => {
    // Transient loading from update()/initial fetch — do not touch toast state.
    if (status === 'loading') return

    const clearShowTimer = () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current)
        showTimerRef.current = null
      }
    }
    const clearRecoveryTimer = () => {
      if (recoveryTimerRef.current) {
        clearTimeout(recoveryTimerRef.current)
        recoveryTimerRef.current = null
      }
    }

    if (hasToken) {
      clearShowTimer()
      clearRecoveryTimer()
      recoveryTimerRef.current = setTimeout(() => {
        setShow(false)
        setDismissed(false)
      }, RECOVERY_DEBOUNCE_MS)
      return () => clearRecoveryTimer()
    }

    clearRecoveryTimer()

    if (needsReauth && !dismissed) {
      clearShowTimer()
      showTimerRef.current = setTimeout(() => setShow(true), SHOW_DELAY_MS)
    } else {
      clearShowTimer()
    }

    return () => clearShowTimer()
  }, [needsReauth, hasToken, dismissed, status])

  const visible = show && !dismissed

  useEffect(() => {
    if (!visible) {
      hideGuardToast(GUARD_TOAST_ID.session)
      return
    }
    showSessionToast({
      onReauth: () => void signOut({ callbackUrl: '/login' }),
      // "Later" and the toast's × both land here.
      onLater: () => setDismissed(true),
    })
  }, [visible])

  // Unmount only -- see the effect above.
  useEffect(() => () => hideGuardToast(GUARD_TOAST_ID.session), [])

  return null
}
